import {
    CalendarIcon,
    CheckIcon,
    ClockIcon,
    EyeIcon,
    MapPinIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
    UserGroupIcon,
    XMarkIcon,
    ListBulletIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import { apiService, Class, CalendarEvent } from '../services/api';
import TabNavigation from '../components/TabNavigation';
import clsx from 'clsx';


const ClassesPage: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  usePageTitle('Class Management');
  
  const [activeView, setActiveView] = useState<'calendar' | 'list'>('calendar');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [classBookings, setClassBookings] = useState<Array<{ id: number; user_id: number; member_name: string; member_email: string; booking_date: string }>>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [selectedMember, setSelectedMember] = useState<{ id: number; name: string; email: string } | null>(null);
  const [showWorkoutModal, setShowWorkoutModal] = useState(false);
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [viewingClass, setViewingClass] = useState<Class | null>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEventDate, setSelectedEventDate] = useState<Date | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'reminder' as 'holiday' | 'reminder' | 'event',
    color: '#3b82f6',
    is_all_day: true,
    start_time: '',
    end_time: ''
  });
  const [showCreateClassType, setShowCreateClassType] = useState(false);
  const [showCreateLocation, setShowCreateLocation] = useState(false);
  const [showManagementModal, setShowManagementModal] = useState(false);
  const [managementTab, setManagementTab] = useState<'class-types' | 'locations' | 'classes'>('class-types');
  const [classTypes, setClassTypes] = useState<{ id: number; name: string; description?: string }[]>([]);
  const [locations, setLocations] = useState<{ id: number; name: string; address: string }[]>([]);
  const [instructors, setInstructors] = useState<{ id: number; first_name: string; last_name: string; email: string; phone?: string; bio?: string }[]>([]);
  
  // Edit states
  const [editingClassType, setEditingClassType] = useState<{ id: number; name: string; description?: string } | null>(null);
  const [editingLocation, setEditingLocation] = useState<{ id: number; name: string; address: string } | null>(null);
  const [editClassType, setEditClassType] = useState({ name: '', description: '' });
  const [editLocation, setEditLocation] = useState({ name: '', address: '' });
  
  // Delete confirmation states
  const [showDeleteClassType, setShowDeleteClassType] = useState<{ id: number; name: string } | null>(null);
  const [showDeleteLocation, setShowDeleteLocation] = useState<{ id: number; name: string } | null>(null);
  const [showDeleteClass, setShowDeleteClass] = useState<{ id: number; name: string } | null>(null);
  
  // Success modal states
  const [showSuccess, setShowSuccess] = useState<{ message: string; type: 'create' | 'update' | 'delete' } | null>(null);
  const [showError, setShowError] = useState<string | null>(null);
  
  const [newClassType, setNewClassType] = useState({
    name: '',
    description: ''
  });
  
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: ''
  });

  const [newClass, setNewClass] = useState({
    class_type_id: 0,
    instructor_ids: [] as number[],
    location_id: 0,
    date: '',
    start_time: '',
    end_time: '',
    max_capacity: 20,
    isRecurring: false,
    recurrenceType: 'daily' as 'daily' | 'weekly' | 'custom',
    recurrenceDays: [] as string[],
    endDate: ''
  });

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  // Format time to 12-hour format (API now sends time strings like "09:00")
  const formatTime = (timeString: string) => {
    if (!timeString) return 'N/A';
    
    try {
      // Handle different time formats
      let hours: number, minutes: string;
      
      // If it's already in HH:MM format
      if (timeString.includes(':')) {
        const parts = timeString.split(':');
        hours = parseInt(parts[0], 10);
        minutes = parts[1] || '00';
        
        // Handle invalid hours (like 2013) - extract just the hour part
        if (hours > 23) {
          hours = hours % 24;
        }
      } else {
        // If it's a number, treat as minutes from midnight
        const totalMinutes = parseInt(timeString, 10);
        hours = Math.floor(totalMinutes / 60) % 24;
        minutes = String(totalMinutes % 60).padStart(2, '0');
      }
      
      if (isNaN(hours)) return 'Invalid time';
      
      const ampm = hours >= 12 ? 'pm' : 'am';
      const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHour}:${minutes}${ampm}`;
    } catch (error) {
      console.error('Time formatting error:', error, timeString);
      return 'Invalid time';
    }
  };

  // Load data from API
  useEffect(() => {
    loadData();
  }, []);

  // Check for query parameters to open modals
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      setShowCreateForm(true);
      // Remove the query parameter from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading classes data...');
      
      const [classesData, typesData, locationsData, instructorsData] = await Promise.all([
        apiService.getClasses(),
        apiService.getClassTypes(),
        apiService.getLocations(),
        apiService.getInstructors()
      ]);
      
      console.log('📊 Classes data:', classesData);
      console.log('📊 Class types data:', typesData);
      console.log('📊 Locations data:', locationsData);
      console.log('📊 Instructors data:', instructorsData);
      
      setClasses(classesData);
      setClassTypes(typesData);
      setLocations(locationsData);
      setInstructors(instructorsData);
    } catch (error) {
      console.error('❌ Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClassTypes = async () => {
    try {
      console.log('🔄 Loading class types...');
      const typesData = await apiService.getClassTypes();
      console.log('📊 Class types data:', typesData);
      setClassTypes(typesData);
    } catch (error) {
      console.error('❌ Error loading class types:', error);
    }
  };

  const loadLocations = async () => {
    try {
      console.log('🔄 Loading locations...');
      const locationsData = await apiService.getLocations();
      console.log('📊 Locations data:', locationsData);
      setLocations(locationsData);
    } catch (error) {
      console.error('❌ Error loading locations:', error);
    }
  };

  const handleCreateClass = async () => {
    console.log('🔄 Creating class with data:', newClass);
    
    if (!newClass.class_type_id || newClass.class_type_id === 0) {
      setShowError('Please select a class type');
      return;
    }
    
    if (!newClass.date || !newClass.start_time || !newClass.end_time) {
      setShowError('Please fill in all required fields');
      return;
    }

    try {
      const classData = {
        class_type_id: newClass.class_type_id,
        instructor_ids: newClass.instructor_ids.length > 0 ? newClass.instructor_ids : undefined,
        location_id: newClass.location_id && newClass.location_id !== 0 ? newClass.location_id : undefined,
        date: newClass.date,
        start_time: newClass.start_time,
        end_time: newClass.end_time,
        max_capacity: newClass.max_capacity,
        isRecurring: newClass.isRecurring,
        recurrenceType: newClass.recurrenceType,
        recurrenceDays: newClass.recurrenceDays,
        endDate: newClass.endDate
      };
      
      console.log('📤 Sending class data:', classData);
      const createdClass = await apiService.createClass(classData);
      console.log('✅ Class created successfully:', createdClass);
      
      setClasses([...classes, createdClass]);
      setNewClass({
        class_type_id: 0,
        instructor_ids: [],
        location_id: 0,
        date: '',
        start_time: '',
        end_time: '',
        max_capacity: 20,
        isRecurring: false,
        recurrenceType: 'daily',
        recurrenceDays: [],
        endDate: ''
      });
      setShowCreateForm(false);
      // Remove query parameter if it exists
      if (searchParams.get('action') === 'create') {
        setSearchParams({}, { replace: true });
      }
    } catch (error) {
      console.error('❌ Error creating class:', error);
      setShowError('Error creating class. Please check the console for details.');
    }
  };

  const handleCreateClassType = async () => {
    if (!newClassType.name) {
      setShowError('Please enter a class type name');
      return;
    }
    
    try {
      await apiService.createClassType(newClassType);
      console.log('Class type created:', newClassType);
      
      // Refresh class types list
      await loadClassTypes();
      
      // Reset form and close modal
      setNewClassType({
        name: '',
        description: ''
      });
      setShowCreateClassType(false);
    } catch (error) {
      console.error('Error creating class type:', error);
      setShowError('Failed to create class type. Please try again.');
    }
  };

  const handleCreateLocation = async () => {
    if (!newLocation.name || !newLocation.address) {
      setShowError('Please enter location name and address');
      return;
    }
    
    try {
      await apiService.createLocation(newLocation);
      console.log('Location created:', newLocation);
      
      // Refresh locations list
      await loadLocations();
      
      // Reset form and close modal
      setNewLocation({
        name: '',
        address: ''
      });
      setShowCreateLocation(false);
    } catch (error) {
      console.error('Error creating location:', error);
      setShowError('Failed to create location. Please try again.');
    }
  };

  // Class Type Edit/Delete handlers
  const handleEditClassType = (classType: { id: number; name: string; description?: string }) => {
    setEditingClassType(classType);
    setEditClassType({ name: classType.name, description: classType.description || '' });
  };

  const handleUpdateClassType = async () => {
    if (!editingClassType || !editClassType.name) {
      setShowError('Please fill in all required fields');
      return;
    }

    try {
      const updatedClassType = await apiService.updateClassType(editingClassType.id, editClassType);
      setClassTypes(classTypes.map(ct => ct.id === editingClassType.id ? updatedClassType : ct));
      setEditingClassType(null);
      setEditClassType({ name: '', description: '' });
      setShowSuccess({ message: 'Class type updated successfully!', type: 'update' });
    } catch (error) {
      console.error('Error updating class type:', error);
      setShowError('Failed to update class type. Please try again.');
    }
  };

  const handleDeleteClassType = (classType: { id: number; name: string }) => {
    setShowDeleteClassType(classType);
  };

  const confirmDeleteClassType = async () => {
    if (!showDeleteClassType) return;

    try {
      await apiService.deleteClassType(showDeleteClassType.id);
      setClassTypes(classTypes.filter(ct => ct.id !== showDeleteClassType.id));
      setShowDeleteClassType(null);
      setShowSuccess({ message: 'Class type deleted successfully!', type: 'delete' });
    } catch (error) {
      console.error('Error deleting class type:', error);
      setShowError('Failed to delete class type. Please try again.');
    }
  };

  // Location Edit/Delete handlers
  const handleEditLocation = (location: { id: number; name: string; address: string }) => {
    setEditingLocation(location);
    setEditLocation({ name: location.name, address: location.address });
  };

  const handleUpdateLocation = async () => {
    if (!editingLocation || !editLocation.name || !editLocation.address) {
      setShowError('Please fill in all required fields');
      return;
    }

    try {
      const updatedLocation = await apiService.updateLocation(editingLocation.id, editLocation);
      setLocations(locations.map(loc => loc.id === editingLocation.id ? updatedLocation : loc));
      setEditingLocation(null);
      setEditLocation({ name: '', address: '' });
      setShowSuccess({ message: 'Location updated successfully!', type: 'update' });
    } catch (error) {
      console.error('Error updating location:', error);
      setShowError('Failed to update location. Please try again.');
    }
  };

  const handleDeleteLocation = (location: { id: number; name: string }) => {
    setShowDeleteLocation(location);
  };

  const confirmDeleteLocation = async () => {
    if (!showDeleteLocation) return;

    try {
      await apiService.deleteLocation(showDeleteLocation.id);
      setLocations(locations.filter(loc => loc.id !== showDeleteLocation.id));
      setShowDeleteLocation(null);
      setShowSuccess({ message: 'Location deleted successfully!', type: 'delete' });
    } catch (error) {
      console.error('Error deleting location:', error);
      setShowError('Failed to delete location. Please try again.');
    }
  };

  const handleEditClass = (classToEdit: Class) => {
    setEditingClass(classToEdit);
    setNewClass({
      class_type_id: classToEdit.class_type_id,
      instructor_ids: classToEdit.trainers?.map(t => t.id) || [],
      location_id: classToEdit.location_id || 0,
      date: classToEdit.date,
      start_time: classToEdit.start_time,
      end_time: classToEdit.end_time,
      max_capacity: classToEdit.max_capacity,
      isRecurring: classToEdit.isRecurring || false,
      recurrenceType: classToEdit.recurrenceType || 'daily',
      recurrenceDays: classToEdit.recurrenceDays || [],
      endDate: classToEdit.endDate || ''
    });
    setShowCreateForm(true);
  };

  const handleUpdateClass = async () => {
    console.log('🔄 Starting class update...');
    console.log('📝 Editing class:', editingClass);
    console.log('📝 New class data:', newClass);
    
    if (!editingClass) {
      console.error('❌ No class being edited');
      setShowError('No class selected for editing');
      return;
    }
    
    if (!newClass.class_type_id || !newClass.date || !newClass.start_time || !newClass.end_time) {
      console.error('❌ Missing required fields');
      setShowError('Please fill in all required fields');
      return;
    }

    try {
        const classData = {
          class_type_id: newClass.class_type_id,
          instructor_ids: newClass.instructor_ids.length > 0 ? newClass.instructor_ids : undefined,
          location_id: newClass.location_id || undefined,
          date: newClass.date,
          start_time: newClass.start_time,
          end_time: newClass.end_time,
          max_capacity: newClass.max_capacity,
          isRecurring: newClass.isRecurring,
          recurrenceType: newClass.recurrenceType,
          recurrenceDays: newClass.recurrenceDays,
          endDate: newClass.endDate
        };
      
      console.log('📤 Sending update data:', classData);
      const updatedClass = await apiService.updateClass(editingClass.id, classData);
      console.log('✅ Class updated successfully:', updatedClass);
      
      setClasses(classes.map(c => c.id === editingClass.id ? updatedClass : c));
      setEditingClass(null);
      setNewClass({
        class_type_id: 0,
        instructor_ids: [],
        location_id: 0,
        date: '',
        start_time: '',
        end_time: '',
        max_capacity: 20,
        isRecurring: false,
        recurrenceType: 'daily',
        recurrenceDays: [],
        endDate: ''
      });
      setShowCreateForm(false);
      console.log('✅ Form closed and state reset');
    } catch (error) {
      console.error('Error updating class:', error);
      setShowError('Failed to update class. Please try again.');
    }
  };

  const handleDeleteClass = (classItem: Class) => {
    setShowDeleteClass({ id: classItem.id, name: classItem.class_name });
  };

  const confirmDeleteClass = async () => {
    if (!showDeleteClass) return;

    try {
      await apiService.deleteClass(showDeleteClass.id);
      setClasses(classes.filter(c => c.id !== showDeleteClass.id));
      setShowDeleteClass(null);
      setShowSuccess({ message: 'Class deleted successfully!', type: 'delete' });
      loadData(); // Reload to refresh the list
    } catch (error) {
      console.error('Error deleting class:', error);
      setShowError('Failed to delete class. Please try again.');
      setShowDeleteClass(null);
    }
  };

  const handleRecurrenceDayToggle = (day: string) => {
    const updatedDays = newClass.recurrenceDays.includes(day)
      ? newClass.recurrenceDays.filter(d => d !== day)
      : [...newClass.recurrenceDays, day];
    setNewClass({ ...newClass, recurrenceDays: updatedDays });
  };

  const getRecurrenceDisplay = (classItem: Class) => {
    if (!classItem.isRecurring) return 'One-time class';
    
    switch (classItem.recurrenceType) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return `Weekly (${classItem.recurrenceDays?.map(day => 
          day.charAt(0).toUpperCase() + day.slice(1)
        ).join(', ')})`;
      case 'custom':
        return `Custom (${classItem.recurrenceDays?.map(day => 
          day.charAt(0).toUpperCase() + day.slice(1)
        ).join(', ')})`;
      default:
        return 'Recurring';
    }
  };

  const getStatusColor = (isActive: boolean, currentTheme: 'light' | 'dark' = theme) => {
    const textColor = currentTheme === 'dark' ? '200' : '700';
    const borderColor = currentTheme === 'dark' ? '400' : '500';
    return isActive 
      ? `bg-green-500/20 text-green-${textColor} border-green-${borderColor}/30`
      : `bg-red-500/20 text-red-${textColor} border-red-${borderColor}/30`;
  };

  const getStatusDisplay = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const getTypeColor = (typeName: string, classTypeId?: number, currentTheme: 'light' | 'dark' = theme) => {
    const textColor = currentTheme === 'dark' ? '200' : '700';
    const borderColor = currentTheme === 'dark' ? '400' : '500';
    
    // If we have a classTypeId, use it for consistent colors
    if (classTypeId !== undefined) {
      const colors = [
        `bg-purple-500/20 text-purple-${textColor} border-purple-${borderColor}/30`,
        `bg-red-500/20 text-red-${textColor} border-red-${borderColor}/30`,
        `bg-blue-500/20 text-blue-${textColor} border-blue-${borderColor}/30`,
        `bg-pink-500/20 text-pink-${textColor} border-pink-${borderColor}/30`,
        `bg-orange-500/20 text-orange-${textColor} border-orange-${borderColor}/30`,
        `bg-green-500/20 text-green-${textColor} border-green-${borderColor}/30`,
        `bg-yellow-500/20 text-yellow-${textColor} border-yellow-${borderColor}/30`,
        `bg-indigo-500/20 text-indigo-${textColor} border-indigo-${borderColor}/30`,
        `bg-cyan-500/20 text-cyan-${textColor} border-cyan-${borderColor}/30`,
        `bg-emerald-500/20 text-emerald-${textColor} border-emerald-${borderColor}/30`,
      ];
      return colors[classTypeId % colors.length];
    }
    
    // Fallback to name-based colors
    const colors: { [key: string]: string } = {
      'Yoga': `bg-purple-500/20 text-purple-${textColor} border-purple-${borderColor}/30`,
      'HIIT': `bg-red-500/20 text-red-${textColor} border-red-${borderColor}/30`,
      'Cycling': `bg-blue-500/20 text-blue-${textColor} border-blue-${borderColor}/30`,
      'Pilates': `bg-pink-500/20 text-pink-${textColor} border-pink-${borderColor}/30`,
      'Strength Training': `bg-orange-500/20 text-orange-${textColor} border-orange-${borderColor}/30`,
      'Weight Training': `bg-orange-500/20 text-orange-${textColor} border-orange-${borderColor}/30`,
      'Cardio': `bg-green-500/20 text-green-${textColor} border-green-${borderColor}/30`,
      'Dance': `bg-yellow-500/20 text-yellow-${textColor} border-yellow-${borderColor}/30`,
      'Martial Arts': `bg-indigo-500/20 text-indigo-${textColor} border-indigo-${borderColor}/30`,
      'CrossFit': `bg-red-500/20 text-red-${textColor} border-red-${borderColor}/30`
    };
    return colors[typeName] || `bg-gray-500/20 text-gray-${textColor} border-gray-${borderColor}/30`;
  };

  const loadClassBookings = async (classId: number) => {
    setLoadingBookings(true);
    try {
      // Get class details which includes bookings
      const classDetails: any = await apiService.getClassById(classId);
      if (classDetails.bookings && Array.isArray(classDetails.bookings)) {
        const formattedBookings = classDetails.bookings.map((booking: any) => ({
          id: booking.id,
          user_id: booking.user_id,
          member_name: `${booking.first_name} ${booking.last_name}`,
          member_email: booking.email,
          booking_date: booking.booked_at || booking.booking_date
        }));
        setClassBookings(formattedBookings);
      } else {
        setClassBookings([]);
      }
    } catch (error) {
      console.error('Error loading class bookings:', error);
      setClassBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleClassClick = async (classItem: Class) => {
    setSelectedClass(classItem);
    await loadClassBookings(classItem.id);
  };

  const getClassesForDate = (date: Date) => {
    // Format date as YYYY-MM-DD in local timezone to match API format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];
    
    return classes.filter(c => {
      // Handle both date string formats (with or without time)
      const classDate = c.date.split('T')[0];
      
      // Check for exact date match (one-time classes)
      if (classDate === dateStr && !c.isRecurring) {
        return true;
      }
      
      // Handle recurring classes
      if (c.isRecurring) {
        const startDateStr = c.date.split('T')[0];
        const endDateStr = c.endDate ? c.endDate.split('T')[0] : null;
        
        // Check if date is within the recurrence range (compare date strings)
        if (dateStr < startDateStr) {
          return false; // Before start date
        }
        if (endDateStr && dateStr > endDateStr) {
          return false; // After end date
        }
        
        // Check recurrence type
        if (c.recurrenceType === 'daily') {
          return true; // Daily recurring - show on all dates in range
        } else if (c.recurrenceType === 'weekly' || c.recurrenceType === 'custom') {
          // Check if the day of week matches one of the recurrence days
          if (c.recurrenceDays && c.recurrenceDays.length > 0) {
            return c.recurrenceDays.includes(dayOfWeek);
          }
        }
      }
      
      return false;
    });
  };

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return calendarEvents.filter(event => event.event_date === dateStr);
  };

  const loadCalendarEvents = useCallback(async () => {
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      
      const events = await apiService.getCalendarEvents(startDate, endDate);
      setCalendarEvents(events);
    } catch (error) {
      console.error('Failed to load calendar events:', error);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadCalendarEvents();
  }, [loadCalendarEvents]);

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setSelectedEventDate(date);
    setEditingEvent(null);
    setEventForm({
      title: '',
      description: '',
      event_type: 'reminder',
      color: '#3b82f6',
      is_all_day: true,
      start_time: '',
      end_time: ''
    });
    setShowEventModal(true);
  };

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingEvent(event);
    setSelectedEventDate(new Date(event.event_date));
    setEventForm({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      color: event.color,
      is_all_day: event.is_all_day,
      start_time: event.start_time || '',
      end_time: event.end_time || ''
    });
    setShowEventModal(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim()) {
      setShowError('Please enter an event title');
      return;
    }

    if (!selectedEventDate) {
      setShowError('Please select a date');
      return;
    }

    try {
      const eventDate = selectedEventDate.toISOString().split('T')[0];
      const eventData = {
        title: eventForm.title,
        description: eventForm.description || undefined,
        event_date: eventDate,
        event_type: eventForm.event_type,
        color: eventForm.color,
        is_all_day: eventForm.is_all_day,
        start_time: eventForm.is_all_day ? undefined : eventForm.start_time || undefined,
        end_time: eventForm.is_all_day ? undefined : eventForm.end_time || undefined
      };

      if (editingEvent) {
        await apiService.updateCalendarEvent(editingEvent.id, eventData);
      } else {
        await apiService.createCalendarEvent(eventData);
      }

      await loadCalendarEvents();
      setShowEventModal(false);
      setEditingEvent(null);
      setSelectedEventDate(null);
    } catch (error: any) {
      console.error('Error saving event:', error);
      setShowError(error.response?.data?.error || 'Failed to save event');
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;

    try {
      await apiService.deleteCalendarEvent(editingEvent.id);
      await loadCalendarEvents();
      setShowEventModal(false);
      setEditingEvent(null);
      setSelectedEventDate(null);
    } catch (error: any) {
      console.error('Error deleting event:', error);
      setShowError(error.response?.data?.error || 'Failed to delete event');
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className={clsx(
          "text-4xl font-bold bg-clip-text text-transparent",
          theme === 'dark'
            ? "bg-gradient-to-r from-white to-gray-300"
            : "bg-gradient-to-r from-gray-900 to-gray-700"
        )}>
          Classes
        </h1>
        <p className={clsx(
          "mt-3 text-lg transition-colors",
          theme === 'dark' ? "text-gray-300" : "text-gray-600"
        )}>
          Create and manage your gym classes and schedules
        </p>
      </div>

      {/* Tab Navigation */}
      <TabNavigation
        tabs={[
          {
            id: 'calendar',
            label: 'Calendar View',
            icon: <CalendarIcon className="h-5 w-5" />
          },
          {
            id: 'list',
            label: 'List View',
            icon: <ListBulletIcon className="h-5 w-5" />
          }
        ]}
        activeTab={activeView}
        onTabChange={(tabId) => setActiveView(tabId as 'calendar' | 'list')}
      />

      {/* Calendar View */}
      {activeView === 'calendar' && (
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
          theme === 'dark'
            ? "bg-white/10 border-white/20"
            : "bg-white/80 border-gray-200/50"
        )}>
          <div className="flex items-center justify-between mb-6">
            <h2 className={clsx(
              "text-2xl font-bold transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>
              {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateMonth('prev')}
                className={clsx(
                  "p-2 rounded-lg transition-colors",
                  theme === 'dark'
                    ? "bg-white/10 hover:bg-white/20 text-white"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                )}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setSelectedDate(new Date())}
                className={clsx(
                  "px-4 py-2 rounded-lg text-sm transition-colors",
                  theme === 'dark'
                    ? "bg-white/10 hover:bg-white/20 text-white"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                )}
              >
                Today
              </button>
              <button
                onClick={() => navigateMonth('next')}
                className={clsx(
                  "p-2 rounded-lg transition-colors",
                  theme === 'dark'
                    ? "bg-white/10 hover:bg-white/20 text-white"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                )}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className={clsx(
                "text-center text-sm font-medium py-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-600"
              )}>
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {getDaysInMonth(selectedDate).map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const dateClasses = getClassesForDate(date);
              const dateEvents = getEventsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isSelected = date.toDateString() === selectedDate.toDateString();

              return (
                <div
                  key={date.toISOString()}
                  className={clsx(
                    "aspect-square rounded-lg border-2 p-2 overflow-y-auto transition-colors cursor-pointer",
                    theme === 'dark'
                      ? "bg-white/5 hover:bg-white/10"
                      : "bg-gray-50 hover:bg-gray-100",
                    isToday ? 'border-orange-500' : isSelected ? 'border-indigo-500' : theme === 'dark' ? 'border-white/10' : 'border-gray-200'
                  )}
                  onClick={() => handleDateClick(date)}
                >
                  <div className={clsx(
                    "text-sm font-medium mb-1 transition-colors",
                    isToday ? 'text-orange-400' : theme === 'dark' ? 'text-white' : 'text-gray-900'
                  )}>
                    {date.getDate()}
                  </div>
                  <div className="space-y-1">
                    {/* Show events first */}
                    {dateEvents.slice(0, 2).map(event => (
                      <div
                        key={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                        style={{ 
                          backgroundColor: event.color + '20',
                          borderLeft: `3px solid ${event.color}`,
                          color: event.color
                        }}
                        title={event.title}
                      >
                        {event.is_all_day ? event.title : `${event.start_time} ${event.title}`}
                      </div>
                    ))}
                    {/* Then show classes */}
                    {dateClasses.slice(0, dateEvents.length > 0 ? 2 : 3).map(classItem => (
                      <div
                        key={classItem.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClassClick(classItem);
                        }}
                        className={`text-xs p-1 rounded truncate ${getTypeColor(classItem.class_name, classItem.class_type_id, theme)} cursor-pointer hover:opacity-80`}
                        title={`${classItem.class_name} - ${formatTime(classItem.start_time)}`}
                      >
                        {formatTime(classItem.start_time)} {classItem.class_name}
                      </div>
                    ))}
                    {(dateClasses.length + dateEvents.length) > (dateEvents.length > 0 ? 4 : 3) && (
                      <div className={clsx(
                        "text-xs transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>
                        +{(dateClasses.length + dateEvents.length) - (dateEvents.length > 0 ? 4 : 3)} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Classes List */}
      {activeView === 'list' && (
      <div className={clsx(
        "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
        theme === 'dark'
          ? "bg-white/10 border-white/20"
          : "bg-white/80 border-gray-200/50"
      )}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={clsx(
            "text-2xl font-bold transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>Scheduled Classes</h2>
          <button
            onClick={() => setShowManagementModal(true)}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            Manage
          </button>
        </div>
        
        {loading ? (
          <div className={clsx(
            "text-center py-12 transition-colors",
            theme === 'dark' ? "text-gray-300" : "text-gray-600"
          )}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-lg">Loading classes...</p>
          </div>
        ) : classes.length === 0 ? (
          <div className={clsx(
            "text-center py-12 transition-colors",
            theme === 'dark' ? "text-gray-300" : "text-gray-600"
          )}>
            <CalendarIcon className={clsx(
              "h-16 w-16 mx-auto mb-4 transition-colors",
              theme === 'dark' ? "text-gray-400" : "text-gray-400"
            )} />
            <p className="text-lg">No classes scheduled yet</p>
            <p className="text-sm">Create your first class to get started</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {classes.map((classItem) => (
              <div key={classItem.id} className={clsx(
                "backdrop-blur-lg rounded-xl border p-6 transition-all duration-200",
                theme === 'dark'
                  ? "bg-white/5 border-white/10 hover:bg-white/10"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              )}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className={clsx(
                        "text-xl font-semibold transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>{classItem.class_name}</h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(classItem.class_name, classItem.class_type_id, theme)}`}>
                        {classItem.class_name}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(classItem.is_active, theme)}`}>
                        {getStatusDisplay(classItem.is_active)}
                      </span>
                      {classItem.isRecurring && (
                        <span className={clsx(
                          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-blue-500/20 border-blue-400/30",
                          theme === 'dark' ? "text-blue-200" : "text-blue-700"
                        )}>
                          Recurring
                        </span>
                      )}
                    </div>
                    <p className={clsx(
                      "mb-3 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>{classItem.class_description || 'No description available'}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className={clsx(
                        "flex items-center gap-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>
                        <CalendarIcon className="h-4 w-4 flex-shrink-0" />
                        <div>
                          <div>{classItem.isRecurring ? getRecurrenceDisplay(classItem) : new Date(classItem.date).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div className={clsx(
                        "flex items-center gap-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>
                        <ClockIcon className="h-4 w-4 flex-shrink-0" />
                        <div>
                          {formatTime(classItem.start_time)} - {formatTime(classItem.end_time)}
                        </div>
                      </div>
                      <div className={clsx(
                        "flex items-center gap-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>
                        <UserGroupIcon className="h-4 w-4 flex-shrink-0" />
                        <div>
                          {classItem.current_bookings}/{classItem.max_capacity}
                        </div>
                      </div>
                      <div className={clsx(
                        "flex items-center gap-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>
                        <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                        <div className="truncate" title={classItem.location_name || 'No location'}>
                          {classItem.location_name || 'No location'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 space-y-1">
                      <p className={clsx(
                        "text-sm transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>
                        Trainer{classItem.trainers?.length > 1 ? 's' : ''}: <span className={clsx(
                          "font-medium transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>
                          {classItem.trainers && classItem.trainers.length > 0 
                            ? classItem.trainers.map(trainer => `${trainer.first_name} ${trainer.last_name}`).join(', ')
                            : 'TBD'
                          }
                        </span>
                      </p>
                      {classItem.isRecurring && classItem.endDate && (
                        <p className={clsx(
                          "text-sm transition-colors",
                          theme === 'dark' ? "text-gray-400" : "text-gray-500"
                        )}>Until: <span className={clsx(
                          "font-medium transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>{new Date(classItem.endDate).toLocaleDateString()}</span></p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewingClass(classItem)}
                      className={clsx(
                        "p-2 rounded-lg transition-all duration-200",
                        theme === 'dark'
                          ? "text-gray-300 hover:text-blue-300 hover:bg-blue-500/20"
                          : "text-gray-600 hover:text-blue-600 hover:bg-blue-100"
                      )}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleEditClass(classItem)}
                      className={clsx(
                        "p-2 rounded-lg transition-all duration-200",
                        theme === 'dark'
                          ? "text-gray-300 hover:text-yellow-300 hover:bg-yellow-500/20"
                          : "text-gray-600 hover:text-yellow-600 hover:bg-yellow-100"
                      )}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClass(classItem)}
                      className={clsx(
                        "p-2 rounded-lg transition-all duration-200",
                        theme === 'dark'
                          ? "text-gray-300 hover:text-red-300 hover:bg-red-500/20"
                          : "text-gray-600 hover:text-red-600 hover:bg-red-100"
                      )}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Class Details Modal with Attendees */}
      {selectedClass && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedClass(null);
              setClassBookings([]);
            }
          }}
        >
          <div 
            className={clsx(
              "rounded-2xl border shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto transition-colors",
              theme === 'dark'
                ? "bg-gradient-to-br from-slate-800 to-slate-900 border-white/20"
                : "bg-white border-gray-200"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={clsx(
              "flex items-center justify-between p-6 border-b transition-colors",
              theme === 'dark' ? "border-white/20" : "border-gray-200"
            )}>
              <h2 className={clsx(
                "text-2xl font-bold transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{selectedClass.class_name}</h2>
              <button
                onClick={() => {
                  setSelectedClass(null);
                  setClassBookings([]);
                }}
                className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                )}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Class Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={clsx(
                    "text-sm mb-1 transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>Date</p>
                  <p className={clsx(
                    "font-medium transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>
                    {new Date(selectedClass.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
                <div>
                  <p className={clsx(
                    "text-sm mb-1 transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>Time</p>
                  <p className={clsx(
                    "font-medium transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>
                    {formatTime(selectedClass.start_time)} - {formatTime(selectedClass.end_time)}
                  </p>
                </div>
                {selectedClass.location_name && (
                  <div>
                    <p className={clsx(
                      "text-sm mb-1 transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>Location</p>
                    <p className={clsx(
                      "font-medium transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>{selectedClass.location_name}</p>
                  </div>
                )}
                <div>
                  <p className={clsx(
                    "text-sm mb-1 transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>Capacity</p>
                  <p className={clsx(
                    "font-medium transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>
                    {selectedClass.current_bookings} / {selectedClass.max_capacity}
                  </p>
                </div>
                {selectedClass.trainers && selectedClass.trainers.length > 0 && (
                  <div className="col-span-2">
                    <p className={clsx(
                      "text-sm mb-1 transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>Instructors</p>
                    <p className={clsx(
                      "font-medium transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>
                      {selectedClass.trainers.map(t => `${t.first_name} ${t.last_name}`).join(', ')}
                    </p>
                  </div>
                )}
              </div>

              {/* Attendees List */}
              <div className={clsx(
                "border-t pt-6 transition-colors",
                theme === 'dark' ? "border-white/20" : "border-gray-200"
              )}>
                <h3 className={clsx(
                  "text-xl font-bold mb-4 flex items-center transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>
                  <UserGroupIcon className="h-5 w-5 mr-2" />
                  Attendees ({classBookings.length})
                </h3>
                {loadingBookings ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                    <p className={clsx(
                      "mt-2 transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>Loading attendees...</p>
                  </div>
                ) : classBookings.length === 0 ? (
                  <div className={clsx(
                    "text-center py-8 transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>
                    <UserGroupIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No attendees yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {classBookings.map((booking) => (
                      <div
                        key={booking.id}
                        onClick={() => {
                          setSelectedMember({
                            id: booking.user_id,
                            name: booking.member_name,
                            email: booking.member_email
                          });
                          setShowWorkoutModal(true);
                        }}
                        className={clsx(
                          "rounded-lg p-4 border transition-colors cursor-pointer",
                          theme === 'dark'
                            ? "bg-white/5 border-white/10 hover:bg-white/10"
                            : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={clsx(
                              "font-medium transition-colors",
                              theme === 'dark' ? "text-white" : "text-gray-900"
                            )}>{booking.member_name}</p>
                            <p className={clsx(
                              "text-sm transition-colors",
                              theme === 'dark' ? "text-gray-400" : "text-gray-500"
                            )}>{booking.member_email}</p>
                          </div>
                          <div className="text-right">
                            <p className={clsx(
                              "text-sm transition-colors",
                              theme === 'dark' ? "text-gray-400" : "text-gray-500"
                            )}>
                              Booked: {new Date(booking.booking_date).toLocaleDateString()}
                            </p>
                            <p className={clsx(
                              "text-xs mt-1 transition-colors",
                              theme === 'dark' ? "text-indigo-400" : "text-indigo-600"
                            )}>Click to view workout</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={clsx(
              "flex justify-end p-6 border-t transition-colors",
              theme === 'dark' ? "border-white/20" : "border-gray-200"
            )}>
              <button
                onClick={() => {
                  setSelectedClass(null);
                  setClassBookings([]);
                }}
                className="px-6 py-2 bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 text-white rounded-lg transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Class Modal */}
      {showCreateForm && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateForm(false);
              setEditingClass(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingClass ? 'Edit Class' : 'Create New Class'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingClass(null);
        setNewClass({
          class_type_id: 0,
          instructor_ids: [],
          location_id: 0,
          date: '',
          start_time: '',
          end_time: '',
          max_capacity: 20,
          isRecurring: false,
          recurrenceType: 'daily',
          recurrenceDays: [],
          endDate: ''
        });
                }}
                className="text-gray-300 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Class Type</label>
                <select
                  value={newClass.class_type_id}
                  onChange={(e) => setNewClass({...newClass, class_type_id: parseInt(e.target.value)})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 px-4 py-3"
                >
                  <option value={0} className="bg-gray-800">Select a class type</option>
                  {classTypes.map(type => (
                    <option key={type.id} value={type.id} className="bg-gray-800">{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Trainers (Optional)</label>
                <div className="space-y-2">
                  {instructors.map(instructor => (
                    <label key={instructor.id} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newClass.instructor_ids.includes(instructor.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewClass({
                              ...newClass, 
                              instructor_ids: [...newClass.instructor_ids, instructor.id]
                            });
                          } else {
                            setNewClass({
                              ...newClass, 
                              instructor_ids: newClass.instructor_ids.filter(id => id !== instructor.id)
                            });
                          }
                        }}
                        className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span className="text-white">
                        {instructor.first_name} {instructor.last_name}
                      </span>
                    </label>
                  ))}
                  {instructors.length === 0 && user && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                      <p className="text-blue-200 text-sm mb-2">
                        💡 No instructors added yet. You can assign yourself as the trainer for now.
                      </p>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newClass.instructor_ids.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewClass({
                                ...newClass, 
                                instructor_ids: [...newClass.instructor_ids, user.id]
                              });
                            } else {
                              setNewClass({
                                ...newClass, 
                                instructor_ids: newClass.instructor_ids.filter(id => id !== user.id)
                              });
                            }
                          }}
                          className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                        />
                        <span className="text-white">
                          {user.first_name} {user.last_name} (Gym Owner)
                        </span>
                      </label>
                    </div>
                  )}
                  {instructors.length === 0 && !user && (
                    <p className="text-gray-400 text-sm">No instructors available</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Location (Optional)</label>
                <select
                  value={newClass.location_id}
                  onChange={(e) => setNewClass({...newClass, location_id: parseInt(e.target.value)})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 px-4 py-3"
                >
                  <option value={0} className="bg-gray-800">No location assigned</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id} className="bg-gray-800">
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {newClass.isRecurring ? 'Start Date *' : 'Date *'}
                  </label>
                  <input
                    type="date"
                    value={newClass.date}
                    onChange={(e) => setNewClass({...newClass, date: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 px-4 py-3"
                    required
                  />
                  {newClass.isRecurring && (
                    <p className="text-xs text-gray-400 mt-1">
                      When does this recurring class start?
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={newClass.start_time}
                    onChange={(e) => setNewClass({...newClass, start_time: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 px-4 py-3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">End Time</label>
                  <input
                    type="time"
                    value={newClass.end_time}
                    onChange={(e) => setNewClass({...newClass, end_time: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 px-4 py-3"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Capacity</label>
                <input
                  type="number"
                  value={newClass.max_capacity}
                  onChange={(e) => setNewClass({...newClass, max_capacity: parseInt(e.target.value) || 20})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 px-4 py-3"
                  min="1"
                  max="100"
                />
              </div>

              {/* Recurring Class Options */}
              <div className="border-t border-white/20 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Recurring Schedule</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isRecurring"
                      checked={newClass.isRecurring}
                      onChange={(e) => setNewClass({...newClass, isRecurring: e.target.checked})}
                      className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <label htmlFor="isRecurring" className="text-white font-medium">
                      Make this a recurring class
                    </label>
                  </div>
                  
                  {newClass.isRecurring && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                      <p className="text-blue-200 text-sm">
                        💡 <strong>Recurring classes</strong> will repeat based on your schedule. 
                        The "Start Date" above is when the recurring pattern begins.
                      </p>
                    </div>
                  )}

                  {newClass.isRecurring && (
                    <div className="space-y-4 pl-7">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Recurrence Type</label>
                        <select
                          value={newClass.recurrenceType}
                          onChange={(e) => setNewClass({...newClass, recurrenceType: e.target.value as 'daily' | 'weekly' | 'custom'})}
                          className="w-full bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 px-4 py-3"
                        >
                          <option value="daily" className="bg-gray-800">Daily</option>
                          <option value="weekly" className="bg-gray-800">Weekly</option>
                          <option value="custom" className="bg-gray-800">Custom Days</option>
                        </select>
                      </div>

                      {(newClass.recurrenceType === 'weekly' || newClass.recurrenceType === 'custom') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Select Days</label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {daysOfWeek.map((day) => (
                              <label key={day.value} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={newClass.recurrenceDays.includes(day.value)}
                                  onChange={() => handleRecurrenceDayToggle(day.value)}
                                  className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                                />
                                <span className="text-gray-300 text-sm">{day.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">End Date (Optional)</label>
                        <input
                          type="date"
                          value={newClass.endDate}
                          onChange={(e) => setNewClass({...newClass, endDate: e.target.value})}
                          className="w-full bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 px-4 py-3"
                        />
                        <p className="text-xs text-gray-400 mt-1">Leave empty for no end date</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={editingClass ? handleUpdateClass : handleCreateClass}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                >
                  <CheckIcon className="h-5 w-5" />
                  {editingClass ? 'Update Class' : 'Create Class'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingClass(null);
        setNewClass({
          class_type_id: 0,
          instructor_ids: [],
          location_id: 0,
          date: '',
          start_time: '',
          end_time: '',
          max_capacity: 20,
          isRecurring: false,
          recurrenceType: 'daily',
          recurrenceDays: [],
          endDate: ''
        });
                  }}
                  className="flex-1 bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Class Modal */}
      {viewingClass && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setViewingClass(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Class Details</h2>
              <button
                onClick={() => setViewingClass(null)}
                className="text-gray-300 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <h3 className={clsx(
                  "text-2xl font-semibold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>{viewingClass.class_name}</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getTypeColor(viewingClass.class_name, viewingClass.class_type_id, theme)}`}>
                  {viewingClass.class_name}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(viewingClass.is_active, theme)}`}>
                  {getStatusDisplay(viewingClass.is_active)}
                </span>
                {viewingClass.isRecurring && (
                  <span className={clsx(
                    "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-blue-500/20 border-blue-400/30",
                    theme === 'dark' ? "text-blue-200" : "text-blue-700"
                  )}>
                    Recurring
                  </span>
                )}
              </div>

              <p className="text-gray-300 text-lg">{viewingClass.class_description || 'No description available'}</p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-gray-300">
                    <CalendarIcon className="h-5 w-5" />
                    <div>
                      <div><span className="font-medium">Date:</span> <span className="text-white">{viewingClass.isRecurring ? getRecurrenceDisplay(viewingClass) : new Date(viewingClass.date).toLocaleDateString()}</span></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <ClockIcon className="h-5 w-5" />
                    <span className="font-medium">Time:</span>
                    <span className="text-white">{formatTime(viewingClass.start_time)} - {formatTime(viewingClass.end_time)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <UserGroupIcon className="h-5 w-5" />
                    <span className="font-medium">Capacity:</span>
                    <span className="text-white">{viewingClass.current_bookings}/{viewingClass.max_capacity}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <MapPinIcon className="h-5 w-5" />
                    <span className="font-medium">Location:</span>
                    <span className="text-white">{viewingClass.location_name || 'No location'}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <span className="text-gray-300 font-medium">Trainer{viewingClass.trainers?.length > 1 ? 's' : ''}:</span>
                    <p className="text-white text-lg">
                      {viewingClass.trainers && viewingClass.trainers.length > 0 
                        ? viewingClass.trainers.map(trainer => `${trainer.first_name} ${trainer.last_name}`).join(', ')
                        : 'TBD'
                      }
                    </p>
                  </div>
                  {viewingClass.isRecurring && viewingClass.endDate && (
                    <div>
                      <span className="text-gray-300 font-medium">Recurring Until:</span>
                      <p className="text-white text-lg">{new Date(viewingClass.endDate).toLocaleDateString()}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-300 font-medium">Enrollment Status:</span>
                    <div className="mt-2">
                      <div className="flex justify-between text-sm text-gray-300 mb-1">
                        <span>Enrolled</span>
                        <span>{viewingClass.current_bookings}/{viewingClass.max_capacity}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(viewingClass.current_bookings / viewingClass.max_capacity) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => {
                    setViewingClass(null);
                    handleEditClass(viewingClass);
                  }}
                  className="flex-1 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Edit Class
                </button>
                <button
                  onClick={() => setViewingClass(null)}
                  className="flex-1 bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Class Type Modal */}
      {showCreateClassType && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateClassType(false);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-white mb-6">Create Class Type</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleCreateClassType(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                  <input
                    type="text"
                    value={newClassType.name}
                    onChange={(e) => setNewClassType({...newClassType, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Yoga Flow"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={newClassType.description}
                    onChange={(e) => setNewClassType({...newClassType, description: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Class description..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateClassType(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Create Class Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Location Modal */}
      {showCreateLocation && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateLocation(false);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-white mb-6">Create Location</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleCreateLocation(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                  <input
                    type="text"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation({...newLocation, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Studio A"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address *</label>
                  <textarea
                    value={newLocation.address}
                    onChange={(e) => setNewLocation({...newLocation, address: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Full address..."
                    rows={3}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateLocation(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Create Location
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Class Type Modal */}
      {editingClassType && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditingClassType(null);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-white mb-6">Edit Class Type</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateClassType(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                  <input
                    type="text"
                    value={editClassType.name}
                    onChange={(e) => setEditClassType({...editClassType, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Yoga Flow"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={editClassType.description}
                    onChange={(e) => setEditClassType({...editClassType, description: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Optional description..."
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setEditingClassType(null);
                    setEditClassType({ name: '', description: '' });
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Update Class Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Location Modal */}
      {editingLocation && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditingLocation(null);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold text-white mb-6">Edit Location</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateLocation(); }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                  <input
                    type="text"
                    value={editLocation.name}
                    onChange={(e) => setEditLocation({...editLocation, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Studio A"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address *</label>
                  <textarea
                    value={editLocation.address}
                    onChange={(e) => setEditLocation({...editLocation, address: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Full address..."
                    rows={3}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setEditingLocation(null);
                    setEditLocation({ name: '', address: '' });
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Update Location
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Management Modal */}
      {showManagementModal && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowManagementModal(false);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-3xl font-bold text-white">Manage Resources</h3>
              <button
                onClick={() => setShowManagementModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-8 w-8" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-6 bg-gray-700 p-1 rounded-lg">
              <button
                onClick={() => setManagementTab('class-types')}
                className={`flex-1 py-3 px-4 rounded-md font-medium transition-all duration-200 ${
                  managementTab === 'class-types'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
              >
                Class Types
              </button>
              <button
                onClick={() => setManagementTab('locations')}
                className={`flex-1 py-3 px-4 rounded-md font-medium transition-all duration-200 ${
                  managementTab === 'locations'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
              >
                Locations
              </button>
              <button
                onClick={() => setManagementTab('classes')}
                className={`flex-1 py-3 px-4 rounded-md font-medium transition-all duration-200 ${
                  managementTab === 'classes'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
              >
                Classes
              </button>
            </div>

            {/* Tab Content */}
            <div className="overflow-y-auto max-h-[60vh]">
              {/* Class Types Tab */}
              {managementTab === 'class-types' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-xl font-semibold text-white">Class Types</h4>
                    <button
                      onClick={() => setShowCreateClassType(true)}
                      className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Class Type
                    </button>
                  </div>
                  <div className="grid gap-4">
                    {classTypes.map(type => (
                      <div key={type.id} className="bg-gray-700 rounded-lg p-4 flex justify-between items-center">
                        <div>
                          <h5 className="text-white font-medium">{type.name}</h5>
                          {type.description && (
                            <p className="text-gray-300 text-sm mt-1">{type.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleEditClassType(type)}
                            className="p-2 text-gray-300 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all duration-200"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteClassType(type)}
                            className="p-2 text-gray-300 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Locations Tab */}
              {managementTab === 'locations' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-xl font-semibold text-white">Locations</h4>
                    <button
                      onClick={() => setShowCreateLocation(true)}
                      className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Location
                    </button>
                  </div>
                  <div className="grid gap-4">
                    {locations.map(location => (
                      <div key={location.id} className="bg-gray-700 rounded-lg p-4 flex justify-between items-center">
                        <div>
                          <h5 className="text-white font-medium">{location.name}</h5>
                          <p className="text-gray-300 text-sm mt-1">{location.address}</p>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleEditLocation(location)}
                            className="p-2 text-gray-300 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all duration-200"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteLocation(location)}
                            className="p-2 text-gray-300 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Classes Tab */}
              {managementTab === 'classes' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-xl font-semibold text-white">Classes</h4>
                    <button
                      onClick={() => {
                        setShowManagementModal(false);
                        setShowCreateForm(true);
                      }}
                      className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Class
                    </button>
                  </div>
                  <div className="grid gap-4">
                    {classes.map(classItem => (
                      <div key={classItem.id} className="bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h5 className="text-white font-medium text-lg">{classItem.class_name}</h5>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-300">
                              <span>📅 {new Date(classItem.date).toLocaleDateString()}</span>
                              <span>🕐 {formatTime(classItem.start_time)} - {formatTime(classItem.end_time)}</span>
                              <span>👥 {classItem.current_bookings}/{classItem.max_capacity}</span>
                              {classItem.trainers && classItem.trainers.length > 0 && (
                                <span>👨‍🏫 {classItem.trainers.map(trainer => `${trainer.first_name} ${trainer.last_name}`).join(', ')}</span>
                              )}
                              {classItem.location_name && (
                                <span>📍 {classItem.location_name}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button 
                              onClick={() => {
                                setShowManagementModal(false);
                                handleEditClass(classItem);
                              }}
                              className="p-2 text-gray-300 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all duration-200"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteClass(classItem)}
                              className="p-2 text-gray-300 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all duration-200"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Class Type Confirmation Modal */}
      {showDeleteClassType && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[70]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteClassType(null);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Delete Class Type</h3>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete <strong>"{showDeleteClassType.name}"</strong>? 
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteClassType(null)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteClassType}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Location Confirmation Modal */}
      {showDeleteLocation && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[70]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteLocation(null);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Delete Location</h3>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete <strong>"{showDeleteLocation.name}"</strong>? 
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteLocation(null)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteLocation}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Class Confirmation Modal */}
      {showDeleteClass && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[70]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteClass(null);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Delete Class</h3>
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete <strong>"{showDeleteClass.name}"</strong>? 
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteClass(null)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteClass}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[80]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSuccess(null);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Success!</h3>
              <p className="text-gray-300 mb-6">{showSuccess.message}</p>
              <button
                onClick={() => setShowSuccess(null)}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showError && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-[80]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowError(null);
            }
          }}
        >
          <div 
            className="bg-gray-800 rounded-2xl p-8 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Error</h3>
              <p className="text-gray-300 mb-6">{showError}</p>
              <button
                onClick={() => setShowError(null)}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Member Workout Modal */}
      {showWorkoutModal && selectedMember && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowWorkoutModal(false);
              setSelectedMember(null);
            }
          }}
        >
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Workout for {selectedMember.name}</h2>
                <p className="text-gray-400 text-sm mt-1">{selectedMember.email}</p>
                <p className="text-gray-400 text-sm">
                  Class Date: {selectedClass ? new Date(selectedClass.date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowWorkoutModal(false);
                  setSelectedMember(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4">Assigned Program</h3>
                <p className="text-gray-300">
                  This feature will show the member's assigned program and allow you to modify their workout for this specific day.
                  Changes will only affect this member, not the entire program.
                </p>
                <p className="text-gray-400 text-sm mt-4">
                  <strong>Note:</strong> Full workout management functionality is coming soon. You'll be able to:
                </p>
                <ul className="list-disc list-inside text-gray-400 text-sm mt-2 space-y-1">
                  <li>View their assigned program</li>
                  <li>See today's workout from the program</li>
                  <li>Modify exercises for this specific day</li>
                  <li>Assign a new workout from available programs</li>
                  <li>Changes only affect this member, not the program template</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end mt-6 pt-6 border-t border-white/20">
              <button
                onClick={() => {
                  setShowWorkoutModal(false);
                  setSelectedMember(null);
                }}
                className="px-6 py-2 bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 text-white rounded-lg transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Event Modal */}
      {showEventModal && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEventModal(false);
              setEditingEvent(null);
              setSelectedEventDate(null);
            }
          }}
        >
          <div className={clsx(
            "rounded-2xl shadow-2xl border max-w-md w-full max-h-[90vh] overflow-y-auto transition-colors",
            theme === 'dark'
              ? "bg-slate-800 border-slate-700"
              : "bg-white border-gray-200"
          )}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className={clsx(
                  "text-2xl font-bold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>
                  {editingEvent ? 'Edit Event' : 'New Event'}
                </h2>
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setEditingEvent(null);
                    setSelectedEventDate(null);
                  }}
                  className={clsx(
                    "p-2 rounded-lg transition-colors",
                    theme === 'dark'
                      ? "hover:bg-white/10 text-gray-400 hover:text-white"
                      : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                  )}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    className={clsx(
                      "w-full px-4 py-2 rounded-lg border transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    )}
                    placeholder="Event title"
                  />
                </div>

                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Description
                  </label>
                  <textarea
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    rows={3}
                    className={clsx(
                      "w-full px-4 py-2 rounded-lg border transition-colors resize-none",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    )}
                    placeholder="Event description (optional)"
                  />
                </div>

                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Event Type
                  </label>
                  <select
                    value={eventForm.event_type}
                    onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value as 'holiday' | 'reminder' | 'event' })}
                    className={clsx(
                      "w-full px-4 py-2 rounded-lg border transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    )}
                  >
                    <option value="reminder">Reminder</option>
                    <option value="holiday">Holiday</option>
                    <option value="event">Event</option>
                  </select>
                </div>

                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={eventForm.color}
                      onChange={(e) => setEventForm({ ...eventForm, color: e.target.value })}
                      className="h-10 w-20 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={eventForm.color}
                      onChange={(e) => setEventForm({ ...eventForm, color: e.target.value })}
                      className={clsx(
                        "flex-1 px-4 py-2 rounded-lg border transition-colors font-mono text-sm",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className={clsx(
                    "flex items-center gap-2 cursor-pointer transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    <input
                      type="checkbox"
                      checked={eventForm.is_all_day}
                      onChange={(e) => setEventForm({ ...eventForm, is_all_day: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">All Day Event</span>
                  </label>
                </div>

                {!eventForm.is_all_day && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={clsx(
                        "block text-sm font-medium mb-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={eventForm.start_time}
                        onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                        className={clsx(
                          "w-full px-4 py-2 rounded-lg border transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        )}
                      />
                    </div>
                    <div>
                      <label className={clsx(
                        "block text-sm font-medium mb-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>
                        End Time
                      </label>
                      <input
                        type="time"
                        value={eventForm.end_time}
                        onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                        className={clsx(
                          "w-full px-4 py-2 rounded-lg border transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        )}
                      />
                    </div>
                  </div>
                )}

                {selectedEventDate && (
                  <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>
                      Date
                    </label>
                    <input
                      type="date"
                      value={selectedEventDate.toISOString().split('T')[0]}
                      onChange={(e) => setSelectedEventDate(new Date(e.target.value))}
                      className={clsx(
                        "w-full px-4 py-2 rounded-lg border transition-colors",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      )}
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6 pt-6 border-t" style={{ borderColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                {editingEvent && (
                  <button
                    onClick={handleDeleteEvent}
                    className={clsx(
                      "px-4 py-2 rounded-lg font-medium transition-colors",
                      theme === 'dark'
                        ? "bg-red-500/20 hover:bg-red-500/30 text-red-300"
                        : "bg-red-50 hover:bg-red-100 text-red-700"
                    )}
                  >
                    Delete
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setEditingEvent(null);
                    setSelectedEventDate(null);
                  }}
                  className={clsx(
                    "flex-1 px-4 py-2 rounded-lg font-medium transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 hover:bg-white/20 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  )}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEvent}
                  className={clsx(
                    "flex-1 px-4 py-2 rounded-lg font-medium transition-colors",
                    theme === 'dark'
                      ? "bg-gradient-to-r from-orange-500 to-blue-500 hover:from-orange-600 hover:to-blue-600 text-white"
                      : "bg-gradient-to-r from-orange-500 to-blue-500 hover:from-orange-600 hover:to-blue-600 text-white"
                  )}
                >
                  {editingEvent ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassesPage;